import { useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "./redux/hooks"
import useIsCrossChain from "./useIsCrossChain"
import { useAccount, useNetwork, useSwitchNetwork } from "wagmi"
import { Chain, createPublicClient, createWalletClient, custom, formatUnits, http, parseUnits, toHex } from "viem"
import inscriptionABI from "data/abi"
import { DEF_TOKEN_ADDRESS } from "utils"
import { setWalletWinOpened } from "./redux/slices/walletSlice"
import { getAggregateSwap, postSubmitOrder, sortParams } from "services/commonServices"
import { setLoading, setOpenSlippageWin, setRefreshData } from "./redux/slices/appSlice"
import { setResetInfo } from "./redux/slices/coreInfoSlice"
import toast from "react-hot-toast"

// btnStatus -1 未连接 -2 交易参数不完整 -3 余额不足 -4 交易资产相同 -5 不足5U -6 滑点过高 -7 链不同
const useMainBtn = () => {
    const dispatch = useAppDispatch()
    const { wagmiChains, refreshData } = useAppSelector((state) => state.app)
    const { isConnected } = useAppSelector((state) => state.wallet)
    const { address, connector } = useAccount()
    const { fromToken, fromChain, toChain, toToken, fromAmount, balanceEnough, isMinUsdt, slippage, aggregator, toAddress, routeSummary, fromTokenUsdt } = useAppSelector((state) => state.coreInfo)
    const { isCrossChain, aggregateType } = useIsCrossChain()
    const [btnStatus, setBtnStatus] = useState(-1)
    const [btnText, setBtnText] = useState('Swap now')
    const [btnActive, setBtnActive] = useState(true)
    const { switchNetworkAsync } = useSwitchNetwork()
    const { chain: curChain } = useNetwork()

    const checkBtnStatus = useCallback(async () => {
        if(!isConnected) {
            setBtnStatus(-1)
            return
        }
        if(!(fromChain && toChain && fromToken && toToken && fromAmount && +fromAmount > 0)) {
            setBtnStatus(-2)
            return
        }
        if(curChain?.id !== +fromChain?.network) {
            setBtnStatus(-7)
            return
        }
        // 余额不足
        if(!balanceEnough) {
            setBtnStatus(-3)
            return
        }
        if(fromChain?.nickName === toChain?.nickName && fromToken?.symbol === toToken?.symbol) {
            setBtnStatus(-4)
            return
        }
        if(isMinUsdt) {
            setBtnStatus(-5)
            return
        }
        if(slippage >= 20) {
            setBtnStatus(-6)
            return
        }
        setBtnStatus(1)
    }, [isConnected, fromChain, toChain, fromToken, toToken, fromAmount, balanceEnough, isMinUsdt, slippage, curChain?.id])

    useEffect(() => {
        checkBtnStatus()
    }, [checkBtnStatus])

    useEffect(() => {
        switch(btnStatus) {
            case -1:
                setBtnText('Connect your wallet')
                break;
            case -2:
                setBtnText('Input an amount to swap')
                break;
            case -3:
                setBtnText('Your balance is not enough')
                break;
            case -4:
                setBtnText('You can’t swap identical assets')
                break;
            case -5:
                setBtnText('Swap amount must exceed 5 USDT')
                break;
            case -6:
                setBtnText('Swap now')
                break;
            case -7:
                setBtnText(`Switch chain to ${fromChain?.fullName}`)
                break;
            default:
                setBtnText(`Swap now`)
        }
        if([-2, -3, -4, -5].includes(btnStatus)) {
            setBtnActive(false)
        } else {
            setBtnActive(true)
        }
    }, [btnStatus, fromChain?.fullName])


    // 本币跨链
    const fnBaseCoin = useCallback(async (orderType: string) => {
        try {
            const curWagmiChain = wagmiChains.find(item => item.id === +fromChain?.network)
            if(!curWagmiChain) return
            const provider = await connector?.getProvider()
            const fromAddress = address  as `0x${string}`
            const amount = parseUnits(fromAmount, fromToken?.decimals)
            const minterAddress = '0x456325f2ac7067234dd71e01bebe032b0255e039'

            const publicClient = createPublicClient({
                chain: curWagmiChain,
                transport: http()
            }) as any
            const walletClient = createWalletClient({
                account: fromAddress,
                chain: curWagmiChain,
                transport: custom(provider)
            }) as any
            const hash = await walletClient?.sendTransaction({ 
                account: fromAddress,
                to: minterAddress,
                value: amount
            })
            const txInfo = await publicClient?.waitForTransactionReceipt(
                { 
                  hash: hash,
                }
            )
            if(txInfo.status.toLowerCase() !== 'success') return
            const tempToAddr = toAddress ? toAddress : fromAddress
            const sourceCertsObj = {
                fromAmount: amount.toString(),
                fromIndex: fromToken?.index.toString(),
                fromChain: fromChain?.nickName,
                fromAddr: fromAddress,
                certHash: hash,
                fromPublicKey: '',
                signature: '123456'
            }
            const sourceCertsStr = JSON.stringify(sourceCertsObj)
            let sourceCertsHex = toHex(sourceCertsStr)
            sourceCertsHex = sourceCertsHex.substring(2) as any
            const params = {
                sourceCerts: sourceCertsHex,
                orderType: orderType,
                toIndex: toToken?.index.toString(),
                toChain: toChain?.nickName,
                toAddr: tempToAddr,
                slippage: slippage.toString(),
                execStrategy: '',
                extra: '',
                triggerPrice: '0',
                timeout: '0',
            }
            const freezeParams = Object.freeze(params)
            const raw = sortParams(freezeParams, fromAddress)
            let signature = await walletClient?.signMessage({ 
                account: fromAddress,
                message: raw,
            })
            signature = signature.substring(2)
            const headers = {
                EvmAddress: fromAddress,
                Signature: signature
            }
            await postSubmitOrder(params, headers)
        } catch(e) {
           throw e
        } finally {

        }
    }, [address, connector, fromAmount, fromChain?.network, fromChain?.nickName, fromToken?.decimals, fromToken?.index, slippage, toAddress, toChain?.nickName, toToken?.index, wagmiChains])
    // ERC20币跨链
    const fnCoreServiceByERC20 = useCallback(async (orderType: string) => {
        try {
            const curWagmiChain = wagmiChains.find(item => item.id === +fromChain?.network)
            if(!curWagmiChain) return
            const provider = await connector?.getProvider()
            const fromAddress = address  as `0x${string}`
            const contractAddress = fromToken?.contractAddress as `0x${string}`
            const amount = parseUnits(fromAmount, fromToken?.decimals)
            const minterAddress = '0x456325f2ac7067234dd71e01bebe032b0255e039'

            const publicClient = createPublicClient({
                chain: curWagmiChain,
                transport: http()
            }) as any
            // 准备参数
            let gasPrice = await publicClient?.getGasPrice()
            const gasLimit = await publicClient?.estimateContractGas({
              address: contractAddress,
              abi: inscriptionABI,
              functionName: 'transfer',
              account: fromAddress,
              gasPrice: gasPrice,
              args: [minterAddress, amount]
            })

            // 合约准备
            const walletClient = createWalletClient({
                account: fromAddress,
                chain: curWagmiChain,
                transport: custom(provider)
            }) as any
            const hash = await walletClient?.writeContract({
              address: contractAddress,
              abi: inscriptionABI,
              functionName: 'transfer',
              account: fromAddress,
              chain: curWagmiChain,
              gasPrice: gasPrice,
              gas: gasLimit,
              args: [minterAddress, amount]
            })
            const txInfo = await publicClient?.waitForTransactionReceipt( 
                { 
                  hash: hash,
                }
              )
            if(txInfo.status.toLowerCase() !== 'success') return
            const tempToAddr = toAddress ? toAddress : fromAddress
            const sourceCertsObj = {
                fromAmount: amount.toString(),
                fromIndex: fromToken?.index.toString(),
                fromChain: fromChain?.nickName,
                fromAddr: fromAddress,
                certHash: hash,
                fromPublicKey: '',
                signature: '123456'
            }
            const sourceCertsStr = JSON.stringify(sourceCertsObj)
            let sourceCertsHex = toHex(sourceCertsStr)
            sourceCertsHex = sourceCertsHex.substring(2) as any
            const params = {
                sourceCerts: sourceCertsHex,
                orderType: orderType,
                toIndex: toToken?.index.toString(),
                toChain: toChain?.nickName,
                toAddr: tempToAddr,
                slippage: slippage.toString(),
                execStrategy: '',
                extra: '',
                triggerPrice: '0',
                timeout: '0',
            }
            const freezeParams = Object.freeze(params)
            const raw = sortParams(freezeParams, fromAddress)
            let signature = await walletClient?.signMessage({ 
                account: fromAddress,
                message: raw,
            })
            signature = signature.substring(2)
            const headers = {
                EvmAddress: fromAddress,
                Signature: signature
            }
            await postSubmitOrder(params, headers)
        } catch(e) {
            throw e
        } finally {

        }
    }, [wagmiChains, connector, address, fromToken?.contractAddress, fromToken?.decimals, fromToken?.index, fromAmount, toAddress, fromChain?.nickName, fromChain?.network, toToken?.index, toChain?.nickName, slippage])

    // approve 
    const fnApprove = useCallback(async (publicClient: any, walletClient: any, curWagmiChain: Chain, transactionInfo: any) => {
        if(!fromToken) return false
        const contractAddress = fromToken?.contractAddress.toLowerCase()
        const swapContractAddress = transactionInfo.to
        // let approveAmount = transactionInfo.amountOut
        let approveAmount = parseUnits(fromAmount, fromToken.decimals).toString() as any
        if(contractAddress === DEF_TOKEN_ADDRESS.toLowerCase()) {
            return true
        }
        const fromAddress = address
        const result = await publicClient.readContract({
            address: contractAddress,
            abi: inscriptionABI,
            functionName: 'allowance',
            args: [fromAddress, swapContractAddress]
        })
        if(result) {
            const approveAmountBig = BigInt(approveAmount)
            if(result < approveAmountBig) {
                approveAmount = (approveAmountBig - result)
                // console.log('已有approved:', result, '当前需要approved:', approveAmountBig, '需要补充的:', approveAmount)
                const approveAmountStr =  formatUnits(approveAmount, fromToken.decimals)
                approveAmount = parseUnits(approveAmountStr, fromToken.decimals).toString()
            } else {
                return true
            }
        }
        let gasPrice = await publicClient?.getGasPrice()
        const gasLimit = await publicClient?.estimateContractGas({
          address: contractAddress,
          abi: inscriptionABI,
          functionName: 'approve',
          account: fromAddress,
          gasPrice: gasPrice,
          args: [swapContractAddress, approveAmount]
        })
        const hash = await walletClient?.writeContract({
          address: contractAddress,
          abi: inscriptionABI,
          functionName: 'approve',
          account: fromAddress,
          chain: curWagmiChain,
          gasPrice: gasPrice,
          gas: gasLimit,
          args: [swapContractAddress, approveAmount]
        })
        const txInfo = await publicClient?.waitForTransactionReceipt(
            { 
                hash: hash,
            }
        )
        if(txInfo.status.toLowerCase() !== 'success') {
            toast.error('Approval failed. Please try again.', {
                duration: 3000,
                position: 'top-right',
            })
            return false
        }
        // if(approveAmount === '0') {
        //     toast('The previously blocked Approval has been cleared. Please proceed with the transaction again.', {
        //         duration: 4000,
        //         position: 'top-right',
        //     })
        //     return false
        // }
        return true
      }, [fromToken, fromAmount, address])

    // 核心提交
    const sendTransaction = useCallback(async (transactionInfo: any) => {
        const curWagmiChain = wagmiChains.find(item => item.id === +fromChain?.network)
        if(!curWagmiChain) return
        const provider = await connector?.getProvider()
        const fromAddress = address
        const publicClient = createPublicClient({
            chain: curWagmiChain,
            transport: http()
        }) as any
        const walletClient = createWalletClient({
            account: fromAddress,
            chain: curWagmiChain,
            transport: custom(provider)
        }) as any
        const result = await fnApprove(publicClient, walletClient, curWagmiChain, transactionInfo)
        if(!result) return

        let gasPrice = await publicClient?.getGasPrice()
        const gasLimit = await publicClient?.estimateGas({ 
            account: fromAddress,
            to: transactionInfo.to,
            gasPrice: gasPrice,
            data: transactionInfo.data,
            value: transactionInfo.value
        })
        transactionInfo['gas'] = gasLimit.toString()
        
        const hash = await walletClient?.sendTransaction(transactionInfo)
        const txInfo = await publicClient?.waitForTransactionReceipt(
            { 
                hash: hash,
            }
        )
        if(txInfo.status.toLowerCase() !== 'success') return
        toast.success('successfully', {
            duration: 3000,
            position: 'top-right',
        })
    }, [address, connector, fnApprove, fromChain?.network, wagmiChains])

    // aggregate swap
    const fnAggregateSwap = useCallback(async() => {
       try {
            if(fromChain && fromToken && toToken && fromAmount && aggregator && address && slippage) {
                const bigFromAmount = parseUnits(fromAmount, fromToken?.decimals).toString()
                const fromAddress = address  as `0x${string}`
                const receiver = toAddress ? toAddress : address
                const params = {
                    chain: fromChain?.nickName,
                    aggregator: aggregator,
                    fromToken: fromToken?.symbol,
                    toToken: toToken?.symbol,
                    fromAmount: bigFromAmount,
                    fromAddress: fromAddress,
                    receiver: receiver,
                    slippage: slippage,
                    allowPartialFill: true,
                    routeSummary: routeSummary,
                    fromTokenDecimal: fromToken?.decimals.toString(),
                    toTokenDecimal: toToken?.decimals.toString()
                }
                const result = await getAggregateSwap(params)
                if(result && result?.code === 0) {
                    const transactionInfo = result.data
                    // transactionInfo.gas = ((transactionInfo.gas) * 1.6).toString()
                    const hash = await sendTransaction(transactionInfo)
                } else {
                    toast.error(result.msg, {
                        duration: 3000,
                        position: 'top-right',
                    })
                }
            }
       } catch(error) {
            throw error
       }
    }, [address, aggregator, fromAmount, fromChain, fromToken, routeSummary, sendTransaction, slippage, toAddress, toToken])

    // aggregate

    const btnHandler = useCallback(async (skipCheck: string = '0') => {
        if(!(fromToken && fromChain)) return
        if(!btnActive) return
        if(btnStatus === -1) {
            // 链接网络
            dispatch(setWalletWinOpened(true))
            return
        }
        if( btnStatus === -7) {
            // 切换网络
            await switchNetworkAsync?.(+fromChain?.network)
            return
        }
        if(btnStatus === -6 && skipCheck !== 'slippage') {
            // 滑点太高
            dispatch(setOpenSlippageWin(true))
            return
        }
        try {
            dispatch(setLoading(true))
            if(isCrossChain) {
                if(fromToken?.contractAddress === DEF_TOKEN_ADDRESS) {
                    await fnBaseCoin('1')
                } else {
                    await fnCoreServiceByERC20('1')
                }
            } else {
                if(aggregateType === 'swap') {
                    await fnAggregateSwap()
                } else {
                    if(fromToken?.contractAddress === DEF_TOKEN_ADDRESS) {
                        await fnBaseCoin('2')
                    } else {
                        await fnCoreServiceByERC20('2')
                    }
                }
            }
        } catch(error: any) {
            console.log(error)
            const errorStr = JSON.stringify(error)
            let errorMsg = ''
            if(errorStr.includes('shortMessage')) {
                errorMsg = error.shortMessage
            } else {
                errorMsg = 'Service is unavailable, please try again later.'
            }
            if(errorMsg) {
                toast.error(errorMsg, {
                    duration: 3000,
                    position: 'top-right',
                })
            }
        } finally {
            dispatch(setResetInfo())
            dispatch(setRefreshData(refreshData + 1))
            dispatch(setLoading(false))
        }
    }, [fromToken, fromChain, btnActive, btnStatus, dispatch, switchNetworkAsync, isCrossChain, fnBaseCoin, fnCoreServiceByERC20, aggregateType, fnAggregateSwap, refreshData])


    return { btnText, btnStatus, btnActive, fromChain, btnHandler }
}

export default useMainBtn